<template>
  <section id="map-beige2" class="scrolly-overlay">
    <!-- sticky container -->
    <div class="scrolly__sticky" :class="{ interactive: step === '5-3'}">
      <!-- mapbox map -->
      <MapBeige2 />

      <!-- legends -->
      <LegendPower v-show="currentPage === 6.1" />
      <LegendPorts v-show="currentPage === 6.2" />

      <!-- ui instruction -->
      <PageInstruction v-show="step === '5-3' || step === '6-0' ||  step === '6-0-1' || step === '6-0-2' || currentPage === 6.2" />
    </div>

    <!-- step container -->
    <v-container inner class="scrolly__text py-0">
      <v-layout class="step step--shorten">
        <div class="text-block" data-step="5-0" />
      </v-layout>

      <v-layout wrap justify-center align-start class="step">
        <div class="text-block" data-step="5-1">
          <v-container>
            <h2 class="headline mb-3">How is China getting other countries to participate?</h2>

          	<p class="body-2">
          	  On a superficial level, the BRI is meant to create opportunities for economic cooperation. In reality, it is also an attempt by China to grow the country’s global influence by forging alliances through infrastructural investments. Participation of the countries involved is crucial in determining the success of the BRI, and developing nations are China’s easiest targets. Lacking in infrastructure due to poor domestic savings and lack of expertise, China’s offers of loans at concessional funding is extremely attractive.
          	</p>
          </v-container>
        </div>
      </v-layout>

      <v-layout justify-center align-start class="step">
        <div class="text-block" data-step="5-1-2">
          <v-container>
            <h3 class="title mb-3">Bilateral Diplomacy</h3>

          	<p class="body-2">
          	  China’s main approach for getting countries to buy into the BRI has been through <span class="font-weight-bold" style="color: #ff4d3d">bilateral diplomacy.</span> Beijing has long preferred approaching cooperation through this one-to-one method, and continues to do so for the BRI despite its international nature.
          	</p>
          </v-container>
        </div>
      </v-layout>

      <v-layout justify-center align-start class="step">
        <div class="text-block" data-step="5-1-2">
          <v-container>
            <p class="body-2">
              This approach is also most effective where there is a lack of coordination among regional groups, a weakened European Union (EU), and retreating American power. In Southeast Asia, this is apparent. Despite the presence of the Association of Southeast Asian Nations (ASEAN), the nature of the partnership that each of the member states have with China varies.
            </p>
          </v-container>
        </div>
      </v-layout>

      <v-layout justify-center align-start class="step">
        <div class="text-block" data-step="5-2">
          <v-container>
            <p class="body-2">
              Countries such as Cambodia and Laos are more-or-less fully supportive of the BRI, and have already received many long-term loans from China to fund several ambitious developments, notably in hydropower. Others like Singapore and Indonesia are still negotiating a fine balance, acknowledging that the BRI is important but not closing itself off to alliances with other players in the region, such as Japan and the U.S.
            </p>
          </v-container>
        </div>
      </v-layout>

      <v-layout justify-center align-start class="step">
        <div class="text-block" data-step="5-2-1">
          <v-container>
            <h3 class="title mb-3">Multilateralism</h3>

          	<p class="body-2">
              While China has a preferred <i>modus operandi</i>, it has not turned away from multilateralism. The AIIB is a key example of this.
          	</p>
          </v-container>
        </div>
      </v-layout>

      <v-layout justify-center align-start class="step">
        <div class="text-block" data-step="5-2-2">
          <v-container>
          	<p class="body-2">
              It is also a member of <span class="font-weight-bold" style="color: #2f755c">Brazil Russia India China South Africa (BRICS)</span>, which in 2014 established the New Development Bank to fund infrastructure projects in its member countries—yet another convenient tool for the BRI.
          	</p>
          </v-container>
        </div>
      </v-layout>

      <v-layout justify-center align-start class="step">
        <div class="text-block" data-step="5-2-3">
          <v-container>
          	<p class="body-2">
              China also has a foothold in the <span class="font-weight-bold" style="color: #f17255">Shanghai Cooperation Organisation</span>—the only organisation besides the United Nations in which both India and Pakistan are members. That said, China’s ability to maneuver the SCO has proven a challenge. Russia still has a <a href="https://www.fpri.org/article/2018/09/organization-of-rivals-limits-of-the-shanghai-cooperation-organization/" target="_blank">strong hand in the SCO</a>, and in June 2018, India’s opposition blocked the <a href="https://thewire.in/diplomacy/india-breaks-ranks-with-sco-refuses-to-endorse-chinas-belt-road-initiative" target="_blank">SCO’s unanimous endorsement of the BRI.</a>
          	</p>
          </v-container>
        </div>
      </v-layout>

      <v-layout justify-center align-start class="step">
        <div class="text-block" data-step="5-2-4">
          <v-container>
          	<p class="body-2">
              China is also plugging in areas where other regional alliances are breaking down. In light of a conflicted EU, Beijing has created the <span class="font-weight-bold" style="color: #868ad5">“16+1 Initiative”</span> that draws together <a href="https://thediplomat.com/2017/11/chinas-charm-offensive-in-eastern-europe-challenges-eu-cohesion/" target="_blank">16 Central and Eastern European countries.</a> As these European states turn to Beijing for economic growth, there is uneasiness in the EU that the BRI will undermine EU’s agenda for trade liberalisation.
          	</p>
          </v-container>
        </div>
      </v-layout>

      <v-layout justify-center align-start class="step">
        <div class="text-block" data-step="5-2-5">
          <v-container>
          	<p class="body-2">
              A similar phenomenon is also taking place with the Mekong River Commission, which has shown that it is ineffectual in getting member states to the negotiating table to deliberate on their shared river resource. In response, China has established the <span class="font-weight-bold" style="color: #3f2dc8">Lancang-Mekong Cooperation</span> instead, which has rolled out a <a href="http://www.scmp.com/news/china/diplomacy-defence/article/2127387/five-things-know-about-lancang-mekong-cooperation" target="_blank">five-year development plan</a> and sees participation from all Mekong countries.
          	</p>
          </v-container>
        </div>
      </v-layout>

      <v-layout justify-center align-end class="step" style="height: 100%;">
        <div class="text-block" data-step="5-2-5">
          <v-container>
            <p class="body-2">
              In recent months, China has also temporarily shifted its focus to cultivating the African and Middle-eastern countries. In July at a China-Arab States Cooperation Forum, Beijing pledged <a href="https://sputniknews.com/analysis/201807121066303168-china-arab-summit/" target="_blank">US$20 billion in loan</a> to Arab nations for the development of their industries and economies. Just a month later in September at a major summit with African leaders, China pledged another <a href="https://www.reuters.com/article/us-china-africa/chinas-xi-offers-another-60-billion-to-africa-but-says-no-to-vanity-projects-idUSKCN1LJ0C4" target="_blank">US$60 billion</a> in aid and loans to African countries. No doubt, money plays the most vital role in convincing countries to participate in the BRI. For nations that long struggled with development and have been at the mercy of Europe and the U.S., China’s offer of cash with no strings attached is extremely attractive.
            </p>
          </v-container>
        </div>
      </v-layout>

      <v-layout justify-center align-end class="step">
        <div class="xs12 text-block" data-step="5-3" />
      </v-layout>
    </v-container>

    <!-- step container -->
    <v-container inner class="scrolly__text scrolly-text-side py-0 mx-0">

      <v-layout wrap justify-center align-start class="step">
        <div class="text-block" data-step="6-0" />
      </v-layout>

      <v-layout wrap justify-center align-start class="step">
        <div class="text-block" data-step="6-0-1">
          <v-container>
            <h2 class="headline mb-3">With an initiative of this scale and ambition, surely there are issues?</h2>

            <h3 class="title mb-3">Territory</h3>

            <p class="body-2">
              Among the most immediate concerns is territory. Some of the BRI corridors run through contested land.
            </p>
          </v-container>
        </div>
      </v-layout>

       <v-layout wrap justify-center align-start class="step">
        <div class="text-block" data-step="6-0-2">
          <v-container>
            <p class="body-2">
              The most prominent one is Kashmir, where tensions have resurfaced due to the BRI. The China-Pakistan Economic Corridor (CPEC) runs right through the disputed territory. Due to this, India has been the BRI’s loudest naysayer, and bilateral relations between the two nations have been tense. In 2017, this resulted in a <a href="https://thediplomat.com/2017/01/india-and-chinas-tug-of-war-over-nepal/" target="_blank">military standoff</a> at the border of Dokham between China and India.
            </p>
          </v-container>
        </div>
      </v-layout>

      <v-layout wrap justify-center align-start class="step">
        <div class="text-block" data-step="6-0-2">
          <v-container>
            <p class="body-2">
              The <strong>South China Sea</strong> is another elephant in the room in debates about the BRI. The <strong>“Nine-Dash Line”</strong> is a demarcation line used initially by the Republic of China, and subsequently the People’s Republic of China, for their claims in the South China Sea.
            </p>
          </v-container>
        </div>
      </v-layout>

      <v-layout wrap justify-center align-start class="step">
        <div class="text-block" data-step="6-0-3">
          <v-container>
            <p class="body-2">
              Despite the United Nations Convention on the Law of the Sea tribunal ruling in 2016 that China had no historical right to the area demarcated by the nine-dash line, it has had an <a href="https://www.theguardian.com/world/2018/feb/06/photos-beijings-militarisation-south-china-sea-philippines" target="_blank">increasing military presence</a> in the area. While most of the countries involved in the South China Sea have approached this with great caution and avoid open confrontation with Beijing, it has no doubt caused hesitation in readily accepting the BRI.
            </p>
          </v-container>
        </div>
      </v-layout>

      <!-- Power -->
      <v-layout wrap justify-center align-start class="step">
        <div class="text-block" data-step="6-0-3">
          <v-container>
            <h3 class="title mb-3">Waning U.S. Hegemony</h3>

            <p class="body-2">
              Since Donald Trump’s election to the presidency, the U.S.’s foreign policy has changed significantly to become an isolationist one. The power vacuum has presented many opportunities, and China has been fast to move in.
            </p>

            <p class="body-2">
              For example, the Federated States of Micronesia (FSM) have had their security maintained by the U.S. military under a treaty that ends in 2023. China has already laid the groundwork. In March 2017, <a href="http://www.xinhuanet.com/english/2017-03/27/c_136161549_2.htm" target="_blank">FSM and China affirmed their decision</a> to cooperate for the BRI. There are talks that China may replace the U.S.’s presence in Micronesia, breaking its monopoly on the 2nd Island Chain.
            </p>
          </v-container>
        </div>
      </v-layout>

      <v-layout wrap justify-center align-start class="step">
        <div class="text-block" data-step="6-1">
          <v-container>
            <p class="body-2">
              Meanwhile, the former superpower represents a weak defence in the Pacific. In recent months, Secretary of State Mike Pompeo announced that the U.S. would fund <a href="http://www.businessworld.in/article/Pompeo-announces-USD-113-mn-in-new-US-investments-in-Indo-Pacific-/30-07-2018-156268/" target="_blank">US$113 million</a> to advance a “free and open” Indo-Pacific, focused on infrastructure and energy sectors. The amount is dismal compared to what China has put in.
            </p>
          </v-container>
        </div>
      </v-layout>

      <v-layout wrap justify-center align-start class="step">
        <div class="text-block" data-step="6-1-1">
          <v-container>
            <h3 class="title mb-3">Mixed Signals</h3>

            <p class="body-2">
              Most news sources are eager to paint global politics in pro- and anti-U.S. camps, and to depict China as the new giant eager to replace the U.S. as the new global super power. Yet, the situation in Asia Pacific is in considerable flux.
            </p>
          </v-container>
        </div>
      </v-layout>

      <v-layout wrap justify-center align-start class="step">
        <div class="text-block" data-step="6-1">
          <v-container>
            <p class="body-2">
              Japan has also risen to the challenge, and has been pushing back China’s influence over Asia. Being the third largest economy in the world, it has leveraged on its financial might to do this. Despite Trump pulling America out of the Trans-Pacific Partnership (TPP), <a href="https://www.reuters.com/article/us-trade-tpp-japan/trans-pacific-trade-pact-without-u-s-to-be-signed-in-march-japan-idUSKBN1FC0X3" target="_blank">Japan has carried the burden</a> to see it to fruition. It is also investing and making its presence felt in areas similar to China’s BRI, such as <a href="https://www.straitstimes.com/asia/se-asia/japan-will-make-all-out-bid-for-kl-singapore-high-speed-rail-project" target="_blank">bidding for the High Speed Rail project</a> connecting Kuala Lumpur and Singapore.
            </p>
          </v-container>
        </div>
      </v-layout>

      <v-layout wrap justify-center align-start class="step">
        <div class="text-block" data-step="6-2">
          <v-container>
            <p class="body-2">
              Japan has also tentatively taken the lead in establishing a possible alternative to the BRI. <span class="font-weight-bold" style="color: #5251a4;">Australia, the United States, India, and Japan</span> are in talks to create a <a href="https://www.reuters.com/article/us-china-beltandroad-quad/australia-u-s-india-and-japan-in-talks-to-establish-belt-and-road-alternative-report-idUSKCN1G20WG" target="_blank">joint regional infrastructure</a> scheme that can counter the BRI. However, details are still scant, and both Australia and Japan are still cautious—they are careful to not appear to alienate China completely and have on occasion signalled willingness to be part of the BRI.
            </p>
          </v-container>
        </div>
      </v-layout>

      <!-- Ports -->
      <v-layout wrap justify-center align-start class="step">
        <div class="text-block" data-step="6-2-1">
          <v-container>
            <h3 class="title mb-3">Neocolonialism</h3>

            <p class="body-2">
              One of the most worrying aspects of the BRI is that it appears to be a form of neocolonialism. Many have accused China of practicing debt diplomacy under the guise of the BRI, handing out large loans to developing nations that they realistically can never pay back.
            </p>

            <p class="body-2 font-weight-bold">
              The map shows ports where China has had some level of involvement.
            </p>
          </v-container>
        </div>
      </v-layout>

      <v-layout wrap justify-center align-start class="step">
        <div class="text-block" data-step="6-2">
          <v-container>
            <p class="body-2">
              A famously touted example is Sri Lanka’s <strong>Hambantota port</strong>, which is now 85 percent owned by state company China Merchants Port Holdings Co. through a <a href="https://www.nytimes.com/2018/06/25/world/asia/china-sri-lanka-port.html" target="_blank">99-year lease worth US$1.12 billion.</a> Sri Lanka had little choice but to give it up to China due to outstanding debt. The move is reminiscent of the unequal treaties between China and the European powers following its defeat at the Opium Wars. What is perceived by most as a loss of sovereignty has raised alarm bells.
            </p>
          </v-container>
        </div>
      </v-layout>

      <v-layout wrap justify-center align-center class="step">
        <div class="text-block" data-step="6-2">
          <v-container>
            <p class="body-2">
              While Sri Lanka has become the cautionary tale for debt-trap diplomacy, few have paid closer attention to China’s strong presence in ports all around the world. At a glance, it appears as though China owns the coasts of Africa. These rapid developments have placed many countries on edge. Fears of a debt-trap have made even strong allies of the Belt and Road—such as Pakistan—<a href="https://www.straitstimes.com/asia/south-asia/pakistan-rethinks-its-role-in-chinas-belt-and-road-plan-financial-times" target="_blank">question their position</a>. There are also worries that ports with strong Chinese presence may be converted into military outposts.
            </p>
          </v-container>
        </div>
      </v-layout>
    </v-container>

  </section>
</template>

<script>
import * as d3 from 'd3';
import scrollama from 'scrollama';

import { mapGetters } from 'vuex';

// components
import MapBeige2 from '@/components/controllers/MapBeige2.vue';
import LegendPower from '@/components/ui/map/LegendPower.vue';
import LegendPorts from '@/components/ui/map/LegendPorts.vue';
import PageInstruction from '@/components/scrolly/PageInstruction.vue';

export default {
  name: 'SectionMapBeige2',
  components: {
    MapBeige2,
    LegendPower,
    LegendPorts,
    PageInstruction,
  },
  computed: {
    ...mapGetters('view', ['currentPage', 'step']),
  },
  mounted() {
    this.initScrollama();
  },
  methods: {
    initScrollama() {
      // instantiate scrollama
      const scrollerMapBeige2 = scrollama();

      // setup scrollama
      scrollerMapBeige2
        .setup({
          container: '#map-beige2',
          graphic: '#map-beige2 .scrolly__sticky',
          step: '#map-beige2 .scrolly__text .step', // required
          offset: 64 / window.innerHeight, // after navbar height
        })
        .onStepEnter(this.handleStepEnter)
        .onStepExit(this.handleStepExit)
        .onContainerEnter(this.handleContainerEnter)
        .onContainerExit(this.handleContainerExit);
    },
    handleStepEnter(res) {
      // get data-step
      const currentStep = d3
        .select(res.element)
        .select('.text-block')
        .attr('data-step');

      // update view state
      this.$store.dispatch('view/updateStep', currentStep);

      // handle page state
      this.handlePageState(res.direction, currentStep);
    },
    handleStepExit() {},
    handleContainerEnter({ direction }) {
      if (direction === 'down') {
        // update view state
        this.$store.dispatch('view/updatePage', 5);
        this.$store.dispatch('view/updateStep', '5-0');
      }
    },
    handleContainerExit({ direction }) {
      if (direction === 'up') {
        // update view state
        this.$store.dispatch('view/updatePage', 4.2);
      }
    },
    handlePageState(direction, step) {
      if (direction === 'down') {
        switch (step) {
          case '5-1':
            // update page state
            this.$store.dispatch('view/updatePage', 5.1);
            break;

          case '5-2':
            // update page state
            this.$store.dispatch('view/updatePage', 5);
            break;

          case '5-2-1':
            // update page state
            this.$store.dispatch('view/updatePage', 5.2);
            break;

          case '5-2-2':
            // update page state
            this.$store.dispatch('view/updatePage', 5.22);
            break;

          case '5-2-3':
            // update page state
            this.$store.dispatch('view/updatePage', 5.23);
            break;

          case '5-2-4':
            // update page state
            this.$store.dispatch('view/updatePage', 5.24);
            break;

          case '5-2-5':
            // update page state
            this.$store.dispatch('view/updatePage', 5);
            break;

          case '5-3':
            // update page state
            this.$store.dispatch('view/updatePage', 5);
            break;

          case '6-0':
            // update page state
            this.$store.dispatch('view/updatePage', 6);
            break;

          case '6-1':
            // update page state
            this.$store.dispatch('view/updatePage', 6.1);
            break;

          case '6-2':
            // update page state
            this.$store.dispatch('view/updatePage', 6.2);
            break;

          default:
            break;
        }
      } else {
        switch (step) {
          case '5-0':
            // update page state
            this.$store.dispatch('view/updatePage', 5);
            break;

          case '5-1-2':
            // update page state
            this.$store.dispatch('view/updatePage', 5.1);
            break;

          case '5-2':
            // update page state
            this.$store.dispatch('view/updatePage', 5);
            break;

          case '5-2-1':
            // update page state
            this.$store.dispatch('view/updatePage', 5.2);
            break;

          case '5-2-2':
            // update page state
            this.$store.dispatch('view/updatePage', 5.22);
            break;

          case '5-2-3':
            // update page state
            this.$store.dispatch('view/updatePage', 5.23);
            break;

          case '5-2-4':
            // update page state
            this.$store.dispatch('view/updatePage', 5.24);
            break;

          case '5-2-5':
            // update page state
            this.$store.dispatch('view/updatePage', 5);
            break;

          case '5-3':
            // update page state
            this.$store.dispatch('view/updatePage', 5);
            break;

          case '6-0-3':
            // update page state
            this.$store.dispatch('view/updatePage', 6);
            break;

          case '6-1':
            // update page state
            this.$store.dispatch('view/updatePage', 6.1);
            break;

          case '6-2':
            // update page state
            this.$store.dispatch('view/updatePage', 6.2);
            break;

          default:
            break;
        }
      }
    },
  },
};
</script>

<style lang="stylus" scoped>
#map-beige2 {
  .scrolly__text.scrolly-text-side {
    max-width: 35vw;
  }
}
</style>
